"use client";

import { useEffect, useCallback } from "react";

export type KeyboardShortcut = {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  description?: string;
  callback: () => void;
};

interface UseKeyboardShortcutsOptions {
  enabled?: boolean;
}

/**
 * Hook for registering keyboard shortcuts
 * Supports single key and key combinations
 */
export function useKeyboardShortcuts(
  shortcuts: KeyboardShortcut[],
  options: UseKeyboardShortcutsOptions = { enabled: true }
) {
  const { enabled = true } = options;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when user is typing in an input/textarea
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      for (const shortcut of shortcuts) {
        const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const ctrlMatch = shortcut.ctrlKey ? event.ctrlKey : !event.ctrlKey;
        const metaMatch = shortcut.metaKey ? event.metaKey : !event.metaKey;
        const shiftMatch = shortcut.shiftKey ? event.shiftKey : !event.shiftKey;
        const altMatch = shortcut.altKey ? event.altKey : !event.altKey;

        if (
          keyMatch &&
          (shortcut.ctrlKey === undefined || ctrlMatch) &&
          (shortcut.metaKey === undefined || metaMatch) &&
          (shortcut.shiftKey === undefined || shiftMatch) &&
          (shortcut.altKey === undefined || altMatch)
        ) {
          event.preventDefault();
          shortcut.callback();
          break;
        }
      }
    },
    [shortcuts, enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, enabled]);
}

/**
 * Hook for two-key sequence shortcuts (e.g., 'n' then 'j' for new job)
 */
export function useSequentialKeyboardShortcuts(
  shortcuts: Array<{
    sequence: string[];
    description?: string;
    callback: () => void;
  }>,
  options: UseKeyboardShortcutsOptions = { enabled: true }
) {
  const { enabled = true } = options;

  const handleKeySequence = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when user is typing in an input/textarea
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const key = event.key.toLowerCase();

      // Store the key sequence in a data attribute or state
      const lastKey = document.body.dataset.lastKey;
      const lastKeyTime = parseInt(document.body.dataset.lastKeyTime || "0");
      const currentTime = Date.now();

      // Reset sequence if more than 1 second has passed
      if (currentTime - lastKeyTime > 1000) {
        document.body.dataset.lastKey = key;
        document.body.dataset.lastKeyTime = currentTime.toString();
        return;
      }

      // Check if the current sequence matches any shortcut
      for (const shortcut of shortcuts) {
        if (
          shortcut.sequence.length === 2 &&
          lastKey === shortcut.sequence[0] &&
          key === shortcut.sequence[1]
        ) {
          event.preventDefault();
          shortcut.callback();
          document.body.dataset.lastKey = "";
          document.body.dataset.lastKeyTime = "0";
          return;
        }
      }

      // Update the last key
      document.body.dataset.lastKey = key;
      document.body.dataset.lastKeyTime = currentTime.toString();
    },
    [shortcuts, enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener("keydown", handleKeySequence);
    return () => {
      window.removeEventListener("keydown", handleKeySequence);
    };
  }, [handleKeySequence, enabled]);
}
